import { useEffect, useRef, type ReactNode } from 'react';
import { useShortcuts } from '../lib/keys';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  widthClassName?: string;
}

/** Focus-trapped modal. Esc closes it (shadowing any lower-priority Esc binding). */
export function Modal({ open, onClose, title, children, footer, widthClassName = 'max-w-lg' }: ModalProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useShortcuts(
    `Modal: ${title}`,
    open
      ? [
          {
            id: 'close',
            bindings: [{ key: 'Escape' }],
            description: 'Close this dialog',
            handler: () => onClose(),
            allowInInputs: true,
          },
        ]
      : [],
  );

  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement;
    const container = containerRef.current;
    const first = container?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    (first ?? container)?.focus();
    return () => {
      previouslyFocused.current?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key !== 'Tab') return;
    const container = containerRef.current;
    if (!container) return;
    const focusable = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
      (el) => el.offsetParent !== null,
    );
    if (focusable.length === 0) return;
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 pt-[10vh]" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        className={`w-full ${widthClassName} rounded-[3px] border border-[var(--border-strong)] bg-[var(--bg-elevated)] outline-none`}
      >
        <div className="flex items-center justify-between border-b border-[var(--border)] px-3 py-2">
          <h2 className="text-[13px] font-semibold">{title}</h2>
          <span className="kbd">Esc</span>
        </div>
        <div className="max-h-[70vh] overflow-y-auto px-3 py-3">{children}</div>
        {footer && <div className="flex justify-end gap-2 border-t border-[var(--border)] px-3 py-2">{footer}</div>}
      </div>
    </div>
  );
}
